/**
 * v4.34.13 — Top-bar navigation shell.
 *
 * Renders only when the user's nav-layout preference is 'top' AND
 * the viewport is ≥ 1024 px. Below that, `<Layout />` falls back to
 * the sidebar drawer regardless of preference (intentional —
 * mobile horizontal nav is poor UX).
 *
 * Structure:
 *   [Brand] [Operational Base] [Card Fleet Ops] [Locations]
 *   [Analytics] [Functions ▼] [Help & Insights]   [Org] [Theme] [User]
 *
 * Sources nav items from `lib/navItems.js` — the same source the
 * sidebar uses. Adding a future module to `navItems.js` makes it
 * appear in both shells automatically.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth';
import { useBranding } from '../../lib/branding';
import { useSurveysModuleEnabled } from '../../modules/surveys/hooks/useSurveysModuleEnabled';
import {
    getPrimaryTopNavItems, getFunctionsItems,
} from '../../lib/navItems';
import { canSeeOperationalBase } from '../../lib/obVisibility';
import { CC_VERTICAL_EVENT } from '../../lib/vertical';
import PortalBrandMark from '../branding/PortalBrandMark';
import { HelpMenu } from '../QuickTour';
import HelperLauncher from '../helper/HelperLauncher';
import LanguageToggle from '../LanguageToggle';
import OrgSwitcher from './OrgSwitcher';
import OpsManualLink from './OpsManualLink';
import CommandCenterButton from './CommandCenterButton';
import { Button } from '../ui/button';
import {
    ChevronDown, ChevronUp, LogOut, Sparkles, Moon, Sun, PanelLeft, UserCog,
} from 'lucide-react';
import { useTheme } from '../../lib/ThemeContext';
import { useNavLayout } from '../../lib/NavLayoutContext';

const FunctionsDropdown = ({ items, onItemClick }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (!items || items.length === 0) return null;

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={open ? t('nav.layout.functionsAria.collapse') : t('nav.layout.functionsAria.expand')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 ${
                    open
                        ? 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-slate-100'
                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                }`}
                data-testid="topnav-functions-trigger"
            >
                <span>{t('nav.layout.functions')}</span>
                {open
                    ? <ChevronUp className="w-3.5 h-3.5" />
                    : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {open && (
                <div
                    role="menu"
                    aria-label={t('nav.layout.functions')}
                    className="absolute left-0 mt-2 w-72 max-h-[70vh] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl z-50 p-1.5"
                    data-testid="topnav-functions-menu"
                >
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                role="menuitem"
                                onClick={() => { setOpen(false); onItemClick?.(); }}
                                className={({ isActive }) =>
                                    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200'
                                            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                                    }`
                                }
                                data-testid={`topnav-functions-item-${item.id}`}
                                data-insight-id={item.insightId}
                                data-insight-label={item.labelKey}
                            >
                                <Icon className={`w-4 h-4 shrink-0 ${item.color || ''}`} />
                                <span className="truncate">{t(item.labelKey)}</span>
                            </NavLink>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default function TopNav() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, logout, isSuperAdmin, isPartner, isClientAdmin, isLocationManager, canManageLocations, hasPermission } = useAuth();
    // Org-level switch for clients; personal "Show Operational Base"
    // setting for super admins (default OFF).
    const obEnabled = canSeeOperationalBase(user);
    const { branding } = useBranding();
    const { theme, toggleTheme } = useTheme();
    const { setNavLayout } = useNavLayout();
    const surveysEnabled = useSurveysModuleEnabled();

    // Re-render when the Command Center resolves a different vertical for the
    // current scope (see Sidebar for the same listener).
    const [ccTick, setCcTick] = useState(0);
    useEffect(() => {
        const bump = () => setCcTick((n) => n + 1);
        window.addEventListener(CC_VERTICAL_EVENT, bump);
        return () => window.removeEventListener(CC_VERTICAL_EVENT, bump);
    }, []);

    const authCtx = useMemo(() => ({
        user, isSuperAdmin, isPartner, isClientAdmin, isLocationManager,
        canManageLocations, hasPermission, surveysEnabled,
        activeVertical: user?.organization_vertical || null,
        // This shell renders the gradient Command Center button, so the
        // duplicate list entry is suppressed here.
        commandCenterButtonVisible: true,
    }), [user, isSuperAdmin, isClientAdmin, isLocationManager, canManageLocations, hasPermission, surveysEnabled, ccTick]);

    const primaryItems = getPrimaryTopNavItems(authCtx);
    const functionsItems = getFunctionsItems(authCtx);

    const handleLogout = async () => {
        try { await logout(); } finally { navigate('/login'); }
    };

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    useEffect(() => {
        if (!userMenuOpen) return;
        const onClick = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [userMenuOpen]);

    return (
        <header
            className="sticky top-0 z-40 border-b border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur"
            data-testid="topnav-header"
        >
            <div className="flex items-center gap-2 px-4 h-14">
                {/* Brand — points at the Dashboard when the org has Operational
                    Base turned off, so the brand link can never land a client
                    somewhere they don't want. */}
                <NavLink to={obEnabled ? "/operational-base" : "/dashboard"} className="flex items-center mr-2 shrink-0" data-testid="topnav-brand-link">
                    <PortalBrandMark variant="topbar" fallbackToPlatform={true} />
                </NavLink>

                {/* Command Center — the one prominent gradient entry, label kept
                    at every tablet/desktop width. */}
                <div className="mr-2 shrink-0">
                    <CommandCenterButton variant="topnav" />
                </div>

                {/* Operational Base — highlighted shortcut, matches sidebar styling */}
                {obEnabled && (
                <NavLink
                    to="/operational-base"
                    className={({ isActive }) =>
                        `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            isActive
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                        }`
                    }
                    data-testid="topnav-operational-base"
                >
                    <Sparkles className="w-4 h-4" />
                    <span className="hidden xl:inline">{t('nav.operationalBase')}</span>
                </NavLink>
                )}

                {/* Primary items */}
                <nav className="flex items-center gap-1" aria-label={t('nav.layout.section')}>
                    {primaryItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                className={({ isActive }) =>
                                    `flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                        isActive
                                            ? 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-slate-100'
                                            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                                    }`
                                }
                                data-testid={`topnav-item-${item.id}`}
                                data-insight-id={item.insightId}
                                data-insight-label={item.labelKey}
                                title={t(item.labelKey)}
                                aria-label={t(item.labelKey)}
                            >
                                <Icon className={`w-4 h-4 shrink-0 ${item.color || ''}`} />
                                <span className="hidden xl:inline whitespace-nowrap">{t(item.shortLabelKey || item.labelKey)}</span>
                            </NavLink>
                        );
                    })}

                    {/* Functions dropdown */}
                    <FunctionsDropdown items={functionsItems} />

                    {/* Help & Insights */}
                    <div className="ml-1">
                        <HelpMenu />
                    </div>
                    {/* Ask my helper — one tap from any screen. */}
                    <div className="ml-1">
                        <HelperLauncher />
                    </div>
                </nav>

                {/* Right-edge utilities */}
                <div className="ml-auto flex items-center gap-1 shrink-0">
                    <OrgSwitcher />
                    {/* Label-bearing link: icon-only below 2xl so the bar never
                        overflows in FR/ES. Full text stays in its title. */}
                    <div className="[&_span]:hidden 2xl:[&_span]:inline">
                        <OpsManualLink />
                    </div>
                    {/* v4.34.13a — One-click revert to sidebar. Lives
                        next to theme/language toggles so switching the
                        layout matches the affordance pattern users
                        already know from Dark Mode and Language. No
                        need to dig into the user menu. */}
                    <button
                        type="button"
                        onClick={() => setNavLayout('sidebar')}
                        className="hidden lg:inline-flex p-2 rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                        aria-label={t('nav.layout.switchToSidebar')}
                        title={t('nav.layout.switchToSidebar')}
                        data-testid="topnav-switch-to-sidebar"
                    >
                        <PanelLeft className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={toggleTheme}
                        className="p-2 rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                        aria-label={t('theme.toggle')}
                        data-testid="topnav-theme-toggle"
                    >
                        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </button>
                    <LanguageToggle variant="compact-inline" />

                    {/* User menu */}
                    <div className="relative" ref={userMenuRef}>
                        <button
                            type="button"
                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                            aria-haspopup="menu"
                            aria-expanded={userMenuOpen}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                            data-testid="topnav-user-menu-trigger"
                        >
                            <div className="w-7 h-7 rounded-full bg-rose-500/10 text-rose-700 dark:text-rose-300 flex items-center justify-center text-xs font-semibold">
                                {(user?.first_name?.[0] || user?.email?.[0] || '?').toUpperCase()}
                            </div>
                            <span className="hidden lg:inline truncate max-w-[140px]">{user?.email || ''}</span>
                            <ChevronDown className="w-3 h-3" />
                        </button>

                        {userMenuOpen && (
                            <div
                                role="menu"
                                className="absolute right-0 mt-2 w-72 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl z-50 p-2"
                                data-testid="topnav-user-menu"
                            >
                                <div className="px-2 py-1.5 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 mb-2">
                                    <div className="font-medium text-slate-700 dark:text-slate-200 truncate">{user?.email}</div>
                                    <div className="capitalize">{user?.role?.replace?.('_', ' ')}</div>
                                </div>
                                <NavLink
                                    to="/my-settings"
                                    role="menuitem"
                                    onClick={() => setUserMenuOpen(false)}
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                                    data-testid="topnav-my-settings"
                                >
                                    <UserCog className="w-4 h-4" />
                                    {t('nav.mySettings')}
                                </NavLink>
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    role="menuitem"
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                                    data-testid="topnav-logout"
                                >
                                    <LogOut className="w-4 h-4" />
                                    {t('nav.logout', 'Logout')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Branded sub-strip — keeps the visual continuity the
                sidebar has with PortalBrandMark + tagline. */}
            {branding?.tagline && (
                <div className="hidden lg:block px-4 pb-2 text-[11px] text-slate-500 dark:text-slate-400 truncate">
                    {branding.tagline}
                </div>
            )}
        </header>
    );
}
